import type { IAPIRoutes } from "../../../../config";
import type {
  AfterVisitSummaryKind,
  AfterVisitSummarySource,
  AfterVisitSummaryStatus,
  HchbPushStatus,
  IAfterVisitSummaryCoordinationNote,
  IAfterVisitSummaryCoordinationNoteResponse,
  IAfterVisitSummaryPdf,
  IAfterVisitSummaryRepository,
  IAfterVisitSummaryScope,
  IAfterVisitSummaryState,
  IEditAfterVisitSummaryRequest,
  IHchbNoteTypesResponse,
  ISendAfterVisitSummaryRequest,
  IStartAfterVisitSummaryRequest,
  IStartAfterVisitSummaryResponse,
} from "../../../core/ports/AfterVisitSummary";

const SUMMARY_KINDS: AfterVisitSummaryKind[] = [
  "circadia_note",
  "circadia_alert",
];
const SUMMARY_STATUSES: AfterVisitSummaryStatus[] = [
  "deferred",
  "processing",
  "ready",
  "failed",
];
const SUMMARY_SOURCES: AfterVisitSummarySource[] = ["generated", "edited"];
const HCHB_PUSH_STATUSES: HchbPushStatus[] = ["pending", "success", "failed"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const isNullableString = (value: unknown): value is string | null =>
  typeof value === "string" || value === null;

const isNullableNumber = (value: unknown): value is number | null =>
  isNumber(value) || value === null;

const isSummaryKind = (value: unknown): value is AfterVisitSummaryKind =>
  SUMMARY_KINDS.includes(value as AfterVisitSummaryKind);

const isSummaryStatus = (value: unknown): value is AfterVisitSummaryStatus =>
  SUMMARY_STATUSES.includes(value as AfterVisitSummaryStatus);

const isSummarySource = (value: unknown): value is AfterVisitSummarySource =>
  SUMMARY_SOURCES.includes(value as AfterVisitSummarySource);

const isHchbPushStatus = (value: unknown): value is HchbPushStatus =>
  HCHB_PUSH_STATUSES.includes(value as HchbPushStatus);

const isNullableHchbPushStatus = (
  value: unknown
): value is HchbPushStatus | null => value === null || isHchbPushStatus(value);

export const isAfterVisitSummaryState = (
  value: unknown
): value is IAfterVisitSummaryState => {
  if (!isRecord(value)) return false;

  return (
    typeof value.title === "string" &&
    isSummaryKind(value.kind) &&
    typeof value.text === "string" &&
    isSummaryStatus(value.status) &&
    isSummarySource(value.source) &&
    isNumber(value.version) &&
    isNullableString(value.note_date) &&
    isNullableString(value.generation_started_at) &&
    isNullableString(value.generated_at) &&
    isNullableString(value.edited_at) &&
    isNullableNumber(value.generated_by_user_id) &&
    isNullableNumber(value.edited_by_user_id) &&
    isNullableString(value.error) &&
    isNullableNumber(value.coordination_note_id) &&
    isNullableHchbPushStatus(value.hchb_push_status)
  );
};

const detailMessage = (detail: unknown, fallback: string): string => {
  if (typeof detail === "string" && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (!isRecord(item)) return null;
        if (typeof item.msg === "string") return item.msg;
        if (typeof item.message === "string") return item.message;
        return null;
      })
      .filter((message): message is string => Boolean(message));
    if (messages.length) return messages.join("; ");
  }

  if (isRecord(detail)) {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.error === "string") return detail.error;
  }

  return fallback;
};

const inferStatusFromDetail = (detail: unknown): number | undefined => {
  const message = detailMessage(detail, "").toLowerCase();

  if (message.includes("not found")) return 404;
  if (message.includes("changed") && message.includes("reload")) return 409;
  if (
    message.includes("already created") ||
    message.includes("already been created")
  ) {
    return 409;
  }

  return undefined;
};

export class AfterVisitSummaryApiError extends Error {
  readonly status?: number;
  readonly detail?: unknown;

  constructor(message: string, status?: number, detail?: unknown) {
    super(message);
    this.name = "AfterVisitSummaryApiError";
    this.status = status;
    this.detail = detail;
  }
}

const throwIfFastApiError = (response: unknown, fallback: string): void => {
  if (!isRecord(response) || !Object.hasOwn(response, "detail")) return;

  const detail = response.detail;
  throw new AfterVisitSummaryApiError(
    detailMessage(detail, fallback),
    inferStatusFromDetail(detail),
    detail
  );
};

const invalidResponse = (message: string, response: unknown): never => {
  throw new AfterVisitSummaryApiError(message, undefined, response);
};

const fillRoute = (
  template: string,
  params: Record<string, string | number>
): string =>
  Object.entries(params).reduce(
    (route, [key, value]) =>
      route.replace(`{${key}}`, encodeURIComponent(String(value))),
    template
  );

const summaryRoute = (
  template: string,
  scope: IAfterVisitSummaryScope
): string =>
  fillRoute(template, {
    clinic_id: scope.clinicId,
    patient_id: scope.patientId,
    note_id: scope.noteId,
  });

const isStartResponse = (
  response: unknown
): response is IStartAfterVisitSummaryResponse =>
  isRecord(response) &&
  isNumber(response.note_id) &&
  isAfterVisitSummaryState(response.summary);

const isHchbNoteTypesResponse = (
  response: unknown
): response is IHchbNoteTypesResponse =>
  isRecord(response) &&
  typeof response.tenant === "string" &&
  Array.isArray(response.note_types) &&
  response.note_types.every(
    (noteType) =>
      isRecord(noteType) &&
      typeof noteType.id === "string" &&
      typeof noteType.description === "string"
  );

const isCoordinationNote = (
  value: unknown
): value is IAfterVisitSummaryCoordinationNote => {
  if (!isRecord(value)) return false;

  return (
    isNumber(value.id) &&
    isNumber(value.clinic_id) &&
    isNumber(value.patient_id) &&
    isNumber(value.user_id) &&
    typeof value.hchb_tenant === "string" &&
    typeof value.hchb_patient_id === "string" &&
    typeof value.hchb_episode_id === "string" &&
    typeof value.hchb_note_type === "string" &&
    typeof value.hchb_note_type_description === "string" &&
    typeof value.note_message === "string" &&
    typeof value.note_date === "string" &&
    typeof value.note_user === "string" &&
    isHchbPushStatus(value.push_status) &&
    isNumber(value.push_attempts) &&
    isNullableString(value.push_last_error) &&
    isNullableString(value.push_last_attempt_at) &&
    isNullableString(value.pushed_at) &&
    isNullableString(value.hchb_response_detail) &&
    isNumber(value.status) &&
    typeof value.created_at === "string" &&
    typeof value.updated_at === "string"
  );
};

const isCoordinationNoteResponse = (
  response: unknown
): response is IAfterVisitSummaryCoordinationNoteResponse =>
  isRecord(response) && isCoordinationNote(response.note);

const normalizeApiBaseUrl = (baseUrl: string): string => {
  if (!baseUrl) {
    throw new AfterVisitSummaryApiError(
      "VITE_API_GATEWAY_URL_V2 is not configured"
    );
  }
  return baseUrl.replace(/\/+$/, "");
};

const rawApiUrl = (apiOrigin: string, path: string): string => {
  const baseUrl = normalizeApiBaseUrl(apiOrigin);
  const apiPrefix = "/patient-centric-app-v2";
  const normalizedPath = `/${path.replace(/^\/+/, "")}`;

  if (baseUrl.endsWith(apiPrefix) && normalizedPath.startsWith(apiPrefix)) {
    return `${baseUrl}${normalizedPath.slice(apiPrefix.length)}`;
  }

  return `${baseUrl}${normalizedPath}`;
};

const filenameFromDisposition = (
  disposition: string | null,
  fallback: string
): string => {
  if (!disposition) return fallback;

  const encoded = disposition.match(
    /filename\*\s*=\s*(?:UTF-8'')?("?)([^";]+)\1/i
  );
  const plain = disposition.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);
  const candidate = (encoded?.[2] || plain?.[1] || plain?.[2])?.trim();
  if (!candidate) return fallback;

  try {
    return decodeURIComponent(candidate);
  } catch {
    return candidate;
  }
};

const throwRawResponseError = async (response: Response): Promise<never> => {
  const rawBody = await response.text();
  let body: unknown = rawBody || undefined;

  if (rawBody) {
    try {
      body = JSON.parse(rawBody);
    } catch {
      // Preserve non-JSON response text as the error detail.
    }
  }

  const detail =
    isRecord(body) && Object.hasOwn(body, "detail") ? body.detail : body;
  throw new AfterVisitSummaryApiError(
    detailMessage(detail, `PDF download failed with HTTP ${response.status}`),
    response.status,
    detail
  );
};

export const AfterVisitSummaryRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
  ApiOrigin,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
  ApiOrigin: string;
}): IAfterVisitSummaryRepository => {
  const { GET, PATCH, POST } = APIProvider;

  const startAfterVisitSummaryAPI = async (
    scope: IAfterVisitSummaryScope,
    request: IStartAfterVisitSummaryRequest
  ): Promise<IStartAfterVisitSummaryResponse> => {
    const response: unknown = await POST(
      summaryRoute(APIRoutes.apiAfterVisitSummary, scope),
      request
    );
    throwIfFastApiError(response, "Failed to start after-visit summary");
    if (!isStartResponse(response)) {
      return invalidResponse(
        "Invalid after-visit summary start response",
        response
      );
    }
    return response;
  };

  const getAfterVisitSummaryAPI = async (
    scope: IAfterVisitSummaryScope
  ): Promise<IAfterVisitSummaryState> => {
    const response: unknown = await GET(
      summaryRoute(APIRoutes.apiAfterVisitSummary, scope)
    );
    throwIfFastApiError(response, "Failed to load after-visit summary");
    if (!isAfterVisitSummaryState(response)) {
      return invalidResponse("Invalid after-visit summary response", response);
    }
    return response;
  };

  const editAfterVisitSummaryAPI = async (
    scope: IAfterVisitSummaryScope,
    request: IEditAfterVisitSummaryRequest
  ): Promise<IAfterVisitSummaryState> => {
    const response: unknown = await PATCH(
      summaryRoute(APIRoutes.apiAfterVisitSummary, scope),
      request
    );
    throwIfFastApiError(response, "Failed to edit after-visit summary");
    if (!isAfterVisitSummaryState(response)) {
      return invalidResponse(
        "Invalid after-visit summary edit response",
        response
      );
    }
    return response;
  };

  const downloadAfterVisitSummaryPdfAPI = async (
    scope: IAfterVisitSummaryScope
  ): Promise<IAfterVisitSummaryPdf> => {
    const path = summaryRoute(APIRoutes.apiAfterVisitSummaryPdf, scope);
    const response = await fetch(rawApiUrl(ApiOrigin, path), {
      headers: {
        Authorization: `Bearer ${await AuthService.getAuthenticationToken()}`,
      },
    });

    if (!response.ok) await throwRawResponseError(response);

    return {
      blob: await response.blob(),
      filename: filenameFromDisposition(
        response.headers.get("Content-Disposition"),
        `after_visit_summary_${scope.noteId}.pdf`
      ),
    };
  };

  const getHchbCoordinationNoteTypesAPI = async (
    clinicId: number,
    patientId: number
  ): Promise<IHchbNoteTypesResponse> => {
    const response: unknown = await GET(
      fillRoute(APIRoutes.apiHchbCoordinationNoteTypes, {
        clinic_id: clinicId,
        patient_id: patientId,
      })
    );
    throwIfFastApiError(
      response,
      "Failed to load HCHB coordination-note types"
    );
    if (!isHchbNoteTypesResponse(response)) {
      return invalidResponse(
        "Invalid HCHB coordination-note types response",
        response
      );
    }
    return response;
  };

  const sendAfterVisitSummaryAPI = async (
    scope: IAfterVisitSummaryScope,
    request: ISendAfterVisitSummaryRequest
  ): Promise<IAfterVisitSummaryCoordinationNoteResponse> => {
    const response: unknown = await POST(
      summaryRoute(APIRoutes.apiAfterVisitSummarySend, scope),
      request
    );
    throwIfFastApiError(response, "Failed to send after-visit summary");
    if (!isCoordinationNoteResponse(response)) {
      return invalidResponse(
        "Invalid after-visit summary send response",
        response
      );
    }
    return response;
  };

  const retryCoordinationNoteAPI = async (
    clinicId: number,
    patientId: number,
    coordinationNoteId: number
  ): Promise<IAfterVisitSummaryCoordinationNoteResponse> => {
    const response: unknown = await POST(
      fillRoute(APIRoutes.apiRetryCoordinationNote, {
        clinic_id: clinicId,
        patient_id: patientId,
        coordination_note_id: coordinationNoteId,
      }),
      {}
    );
    throwIfFastApiError(
      response,
      "Failed to retry HCHB coordination-note push"
    );
    if (!isCoordinationNoteResponse(response)) {
      return invalidResponse(
        "Invalid coordination-note retry response",
        response
      );
    }
    return response;
  };

  return {
    startAfterVisitSummaryAPI,
    getAfterVisitSummaryAPI,
    editAfterVisitSummaryAPI,
    downloadAfterVisitSummaryPdfAPI,
    getHchbCoordinationNoteTypesAPI,
    sendAfterVisitSummaryAPI,
    retryCoordinationNoteAPI,
  };
};
